import { TABLE_TOP_CORNERS } from "../../../v2/components/tableHeaderCorners.js";
// src/features/projects/weekly/ProjectWorkingUpdatesView.jsx
//
// Presentational "Working updates · weekly" column for the project detail
// page. Data flow, posting and history live in the parent; this only renders
// the canonical weekly overview payload for one project.

import { T } from "../../../app/tokens.js";
import { TRACKS, TRACK_STATUS_META, statusForTrack, resolveLine, weekLabel, fmtTimestamp, assignedTrackCompleteness, weeklyTrackState } from "./weeklyModel.js";
import { TrackCompleteness, TrackDot } from "./TrackCompleteness.jsx";

const FOOTER_PREFIX = "These updates appear in the ";
const FOOTER_BOLD = "Weekly Review";
const FOOTER_SUFFIX = " and freeze into the weekly archive.";
const TRACK_KEYS = new Set(TRACKS.map((t) => t.key));
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/** Rows to render: assigned canonical tracks plus unassigned tracks with evidence.
 * Without captured assignments, show recorded evidence only. */
export function detailTracks(project) {
  const c = assignedTrackCompleteness(project);
  if (!c.available) return TRACKS.filter((t) => weeklyTrackState(project, t.key).state !== "not_yet").map((t) => ({ ...t, assigned: null }));
  const keys = new Set(project.assignedTrackKeys);
  return TRACKS.filter((t) => keys.has(t.key) || weeklyTrackState(project, t.key).state !== "not_yet")
    .map((t) => ({ ...t, assigned: keys.has(t.key) }));
}

const isBlank = (s) => typeof s !== "string" || !s.trim();

/** Line belongs to the payload week; omitted line.weekDate means payload-scoped. */
export function lineInWeek(line, weekDate) {
  return !line?.weekDate || String(line.weekDate).slice(0, 10) === weekDate;
}

/** Distinct canonical tracks with at least one nonblank actual line this week. */
export function countReportedTracks(project, weekDate) {
  const seen = new Set();
  for (const l of project?.lines || []) {
    if (!TRACK_KEYS.has(l.trackKey) || !lineInWeek(l, weekDate)) continue;
    const r = resolveLine(l);
    if (!isBlank(r.original) || !isBlank(r.current)) seen.add(l.trackKey);
  }
  return seen.size;
}

/** UTC weekday attribution, e.g. "Mon". */
export function utcDay(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "" : WEEKDAYS[d.getUTCDay()];
}

function authorOf(obj) {
  const name = obj?.authorName;
  return isBlank(name) ? "Unattributed" : name.trim();
}

function findProject(data, projectId) {
  return (data?.projects || []).find((p) => String(p.id) === String(projectId)) || null;
}

const sx = {
  zone: { borderRadius: 14, ...TABLE_TOP_CORNERS, overflow: "hidden", border: `1px solid ${T.projectsWeeklyBorder}`, background: T.card },
  head: { ...TABLE_TOP_CORNERS, background: T.projectsWeeklySurface, borderBottom: `1px solid ${T.projectsWeeklyBorder}`, padding: "11px 16px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10 },
  lab: { fontSize: T.fsXs, letterSpacing: ".06em", textTransform: "uppercase", fontWeight: T.fwHubTitle, color: T.projectsHubLive },
  headBtn: { fontSize: T.fsXs5, fontWeight: T.fwBold, color: T.projectsHubLive, border: `1px solid ${T.projectsWeeklyBorder}`, borderRadius: 7, padding: "4px 10px", background: T.card, cursor: "pointer" },
  body: { padding: 16 },
  wk: { fontSize: T.fsSm, color: T.projectsHubLive, fontWeight: T.fwBold, marginBottom: 10 },
  track: { padding: "9px 0", borderBottom: `1px solid ${T.lightGray}` },
  trow: { display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap" },
  tk: { fontWeight: T.fwBold, fontSize: T.fsBase, color: T.dark, minWidth: 118 },
  pill: (m) => ({ fontSize: T.fsXs, fontWeight: T.fwBold, borderRadius: 999, padding: "1px 8px", color: m.color, background: m.bg }),
  line: { margin: "4px 0 0 18px" },
  note: { fontSize: T.fsSm5, color: T.dark },
  struck: { fontSize: T.fsSm5, color: T.light, textDecoration: "line-through" },
  by: { fontSize: T.fsXxs5, color: T.mid, marginTop: 2 },
  muted: { fontSize: T.fsSm, color: T.mid },
  hist: { margin: "3px 0 0", paddingLeft: 14, fontSize: T.fsXxs5, color: T.mid },
  post: { width: "100%", background: T.projectsHubLive, color: T.white, border: "none", borderRadius: 9, padding: 10, fontWeight: T.fwBold, fontSize: T.fsBase, cursor: "pointer", marginTop: 12 },
  explain: { fontSize: T.fsXs, color: T.mid, marginTop: 6 },
  panel: { fontSize: T.fsSm, color: T.mid, background: T.hoverBg, borderRadius: 8, padding: "10px 12px" },
  err: { fontSize: T.fsSm, color: T.red, background: T.alertErrBg, border: `1px solid ${T.alertErrBorder}`, borderRadius: 8, padding: "10px 12px" },
  retry: { marginLeft: 8, fontSize: T.fsSm, fontWeight: T.fwBold, color: T.red, background: "none", border: `1px solid ${T.alertErrBorder}`, borderRadius: 6, padding: "2px 8px", cursor: "pointer" },
  skel: { height: 14, borderRadius: 6, background: T.lightGray, margin: "10px 0" },
  footer: { fontSize: T.fsXs, color: T.mid, marginTop: 12, borderTop: `1px solid ${T.projectsHubLine}`, paddingTop: 10 },
};

const NEUTRAL = { label: "No status", color: T.light, bg: T.lightGray };

export function LineView({ line }) {
  const r = resolveLine(line);
  const at = line.createdAt;
  return (
    <div style={sx.line} data-testid={`line-${line.id}`}>
      {r.corrected ? (
        <>
          <s style={sx.struck}>{r.original}</s>
          {r.struck ? <div style={sx.muted}>Struck</div> : <div style={sx.note}>{r.current}</div>}
        </>
      ) : (
        <div style={sx.note}>{r.original}</div>
      )}
      <div style={sx.by}>
        {authorOf(line)}{line.authorOrg ? `, ${line.authorOrg}` : ""}{utcDay(at) ? ` · ${utcDay(at)} (UTC)` : " · Day unavailable"}
      </div>
      {r.history.length > 0 && (
        <ul style={sx.hist} data-testid={`history-line-${line.id}`}>
          {r.history.map((c, i) => (
            <li key={c.id ?? i}>
              {c.action === "strike" ? "Struck" : `Corrected to "${c.replacement || ""}"`} by {c.correctorName?.trim() || "Unattributed"}
              {fmtTimestamp(c.createdAt) ? ` · ${fmtTimestamp(c.createdAt)}` : ""}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function ProjectWorkingUpdatesView({ state, projectId, canPost, onPost, onRetry, history }) {
  const { loading, error, data } = state || {};
  const project = data ? findProject(data, projectId) : null;
  const writable = Boolean(data && !data.frozen && !data.historical && data.weekDate === data.currentWeek);
  const showControls = Boolean(canPost && !loading && !error && data && writable && project && onPost);

  let body;
  if (loading) {
    body = <div data-testid="status-updates-loading">{[70, 90, 55].map((w) => <div key={w} style={{ ...sx.skel, width: `${w}%` }} />)}</div>;
  } else if (error) {
    body = (
      <div style={sx.err} role="alert" data-testid="status-updates-error">
        {typeof error === "string" ? error : error.message || "Could not load weekly updates."}
        {onRetry && <button type="button" style={sx.retry} onClick={onRetry} data-testid="button-retry-updates">Retry</button>}
      </div>
    );
  } else if (!data) {
    body = null;
  } else if (data.legacyUnavailable) {
    body = <div style={sx.panel} data-testid="status-updates-legacy">{weekLabel(data.weekDate)} was archived before per-track updates were recorded; no track detail is available.</div>;
  } else if (!project) {
    body = <div style={sx.panel} data-testid="status-updates-not-in-week">This project is not part of the Weekly Review for {weekLabel(data.weekDate).replace(/^Week/, "the week")}.</div>;
  } else {
    const rows = detailTracks(project);
    const lines = (project.lines || []).filter((l) => TRACK_KEYS.has(l.trackKey) && lineInWeek(l, data.weekDate) && (!isBlank(l.note) || !isBlank(resolveLine(l).current)));
    body = (
      <>
        <div style={sx.wk} data-testid="text-updates-week">
          {weekLabel(data.weekDate)}{data.frozen ? " · Frozen archive · read-only" : ""}
        </div>
        <div style={{ marginBottom: 10 }}><TrackCompleteness project={project} surface="working" /></div>
        {lines.length === 0 && <div style={{ ...sx.panel, marginBottom: 8 }} data-testid="status-updates-empty">No updates posted this week.</div>}
        {rows.length === 0 && <div style={sx.panel} data-testid="status-updates-no-tracks">{Array.isArray(project.assignedTrackKeys) ? "No tracks assigned to this project." : "No recorded track evidence for this week."}</div>}
        {rows.map((t, i) => {
          const st = statusForTrack(project, t.key);
          const meta = (st && TRACK_STATUS_META[st.status]) || NEUTRAL;
          const tl = lines.filter((l) => l.trackKey === t.key);
          const ts = weeklyTrackState(project, t.key);
          return (
            <div key={t.key} style={{ ...sx.track, ...(i === rows.length - 1 ? { borderBottom: "none" } : null) }} data-testid={`track-${t.key}`}>
              <div style={sx.trow}>
                <TrackDot row={ts} testId={`dot-track-${t.key}`} />
                <span style={sx.tk}>{t.label}</span>
                <span style={sx.pill(meta)} data-testid={`status-track-${t.key}`}>{meta.label}</span>
                {t.assigned === false && <span style={sx.muted} data-testid={`unassigned-track-${t.key}`}>Not assigned - historical</span>}
              </div>
              {tl.length === 0 ? <div style={{ ...sx.line, ...sx.muted }}>{ts.state === "quiet" ? "Nothing this week" : "No update"}</div> : tl.map((l) => <LineView key={l.id} line={l} />)}
            </div>
          );
        })}
      </>
    );
  }

  return (
    <section style={sx.zone} data-testid="panel-working-updates">
      <div style={sx.head}>
        <span style={sx.lab}>WORKING UPDATES · WEEKLY</span>
        {showControls && <button type="button" style={sx.headBtn} onClick={onPost} data-testid="button-post-update-header">Post update</button>}
      </div>
      <div style={sx.body}>
        {body}
        {showControls && (
          <>
            <button type="button" style={sx.post} onClick={onPost} data-testid="button-post-my-update">+ Post my update for this week</button>
            <div style={sx.explain}>The form checks your track assignments and project linkage; it does not change who may post.</div>
          </>
        )}
        {history}
        <div style={sx.footer} data-testid="text-updates-footer">
          {FOOTER_PREFIX}<b>{FOOTER_BOLD}</b>{FOOTER_SUFFIX}
        </div>
      </div>
    </section>
  );
}
